import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, unref as _unref, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "posos-analysis-widget" }
const _hoisted_2 = { class: "mtop-50" }
const _hoisted_3 = { class: "colCenter" }
const _hoisted_4 = { class: "row-top gap-20" }
const _hoisted_5 = {
  key: 0,
  class: "col gap-20"
}
const _hoisted_6 = {
  key: 0,
  class: "col gap-03"
}
const _hoisted_7 = {
  key: 1,
  class: "col gap-03"
}
const _hoisted_8 = {
  key: 2,
  class: "col gap-03"
}
const _hoisted_9 = {
  key: 3,
  class: "col gap-03"
}

import loadingSpinner from "@/views/loadingSpinner.vue";

import { ref, onMounted, onBeforeUnmount } from "vue";
import pososapi from "@/api/pososapi";
import { usePososAuth } from "@/functions/usePososAuth";
import { useCurrentEntryStore } from '@/stores/currentEntry';
import { useI18n } from "vue-i18n";
import gen from "@/functions/general.js";

const widgetLocale  = "fr_BE";

interface StrengthMismatch {
  prescribedName: string;
  prescribedDose: string;
  approximatedLabel: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'PososAnalysisWidget',
  props: {
    frameHeight: { default: 768 },
    frameWidth: { default: 600 },
    theme: { default: "posos" },
    debug: { type: Boolean, default: false }
  },
  setup(__props: any) {

const { t } = useI18n();
const currentEntryStore = useCurrentEntryStore();
const props = __props;

const containerRef = ref<HTMLElement | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);

const unresolved = ref<{
  drugs?: string[];
  conditions?: string[];
  allergies?: string[];
  strengthMismatches?: StrengthMismatch[];
} | null>(null);

let widgetInstance: any = null;

const { fetchToken } = usePososAuth();

function loadSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if ((window as any).Posos) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = process.env.VUE_APP_POSOS_WIDGET_URL;
    script.type = "text/javascript";
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error("Failed to load POSOS SDK"));
    };
    document.head.appendChild(script);
  });
}

async function initWidget(forceToken = false) {
  loading.value = true;
  error.value = null;
  try {
    // 1. Load the SDK
    await loadSdk();
    // 2. Get auth token from backend
    const authToken = await fetchToken(forceToken);
    // 3. Initialize the widget
    if (!containerRef.value) {
      throw new Error("Widget container not found");
    }
    widgetInstance = new (window as any).Posos.Widget.Analysis(containerRef.value, {
      authToken,
      env: process.env.VUE_APP_POSOS_WIDGET_ENV,
      frameHeight: props.frameHeight,
      frameWidth: props.frameWidth,
      locale: widgetLocale,
      theme: props.theme,
      debug: props.debug,
    });
    // 4. Register token refresh callback
    widgetInstance.onAuthTokenRefresh(fetchToken);
    // 5. Fetch data from backend and auto-trigger analysis
    const analysisData = await pososapi.getAnalysisData(currentEntryStore.currentPatientId);
    widgetInstance.analyze(analysisData.payload);
    // 6. Surface unresolved items so the user sees an incomplete analysis
    unresolved.value = analysisData.unresolved ?? null;
    loading.value = false;
  } catch (err: any) {
    console.error("POSOS widget error:", err);
    error.value = t('posos.error.loading');
    loading.value = false;
  }
}

function destroyWidget() {
  if (widgetInstance) {
    widgetInstance.destroy();
    widgetInstance = null;
  }
}

async function retry() {
  destroyWidget();
  await initWidget(true);
}

onMounted(() => {
  initWidget();
});

onBeforeUnmount(() => {
  destroyWidget();
});

return (_ctx: any,_cache: any) => {
  const _component_text_item = _resolveComponent("text-item")!
  const _component_buttonGroup = _resolveComponent("buttonGroup")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          style: _normalizeStyle({ minHeight: _ctx.frameHeight + 'px' })
        }, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(loadingSpinner, {
              logo: false,
              style: 'w-3',
              class: "mtop-25"
            })
          ])
        ], 4))
      : (error.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: "rowcenter",
            style: _normalizeStyle({ minHeight: _ctx.frameHeight + 'px' })
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(_component_text_item, {
                txt: error.value,
                styles: "main-alert"
              }, null, 8, ["txt"]),
              _createVNode(_component_buttonGroup, {
                styles: "border-m",
                class: "mtop-25"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_text_item, {
                    txt: _unref(t)('action.retry'),
                    onClick: retry
                  }, null, 8, ["txt"])
                ]),
                _: 1
              })
            ])
          ], 4))
        : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", {
        ref_key: "containerRef",
        ref: containerRef
      }, null, 512),
      (unresolved.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (unresolved.value.drugs?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createVNode(_component_text_item, {
                    txt: _unref(t)('posos.unresolved.drugs'),
                    styles: "main-alert"
                  }, null, 8, ["txt"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(unresolved.value.drugs, (item, index) => {
                    return (_openBlock(), _createBlock(_component_text_item, {
                      key: 'drug-' + index,
                      txt: '*) ' + item,
                      class: "text-max-w linebreak"
                    }, null, 8, ["txt"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (unresolved.value.strengthMismatches?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _createVNode(_component_text_item, {
                    txt: _unref(t)('posos.unresolved.strengthMismatches'),
                    styles: "main-alert"
                  }, null, 8, ["txt"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(unresolved.value.strengthMismatches, (item, index) => {
                    return (_openBlock(), _createBlock(_component_text_item, {
                      key: 'strengthMismatch-' + index,
                      txt: '*) ' + _unref(gen).capitalize(item.approximatedLabel),
                      class: "text-max-w linebreak"
                    }, null, 8, ["txt"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (unresolved.value.conditions?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(_component_text_item, {
                    txt: _unref(t)('posos.unresolved.conditions'),
                    styles: "main-alert"
                  }, null, 8, ["txt"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(unresolved.value.conditions, (item, index) => {
                    return (_openBlock(), _createBlock(_component_text_item, {
                      key: 'cond-' + index,
                      txt: '*) ' + item,
                      class: "text-max-w linebreak"
                    }, null, 8, ["txt"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true),
            (unresolved.value.allergies?.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(_component_text_item, {
                    txt: _unref(t)('posos.unresolved.allergies'),
                    styles: "main-alert"
                  }, null, 8, ["txt"]),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(unresolved.value.allergies, (item, index) => {
                    return (_openBlock(), _createBlock(_component_text_item, {
                      key: 'allergy-' + index,
                      txt: '*) ' + item,
                      class: "text-max-w linebreak"
                    }, null, 8, ["txt"]))
                  }), 128))
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true)
    ], 512), [
      [_vShow, !loading.value && !error.value]
    ])
  ]))
}
}

})